import { Link } from "wouter";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";
import RelatedArticles from "@/components/RelatedArticles";

export default function Kantpressning() {
  return (
    <Layout>
      <SEOHead
        title="Kantpressning i Eskilstuna – upp till 310 ton | ASPAB"
        description="Kantpressning med tre kantpressar upp till 310 ton. Hög precision för prototyper och serier. Amada och Ursviken. Begär offert från ASPAB!"
        canonical="https://aspab.com/tjanster/kantpressning"
        path="/tjanster/kantpressning"
        breadcrumbs={[
          { name: "Tjänster", href: "/tjanster" },
          { name: "Kantpressning", href: "/tjanster/kantpressning" },
        ]}
              ogImage={IMAGES.ogKantpressning}
      />

      <PageHero
        title="Kantpressning i Eskilstuna"
        subtitle="Tre kantpressar med upp till 310 tons kapacitet. Vi bockar plåt med hög precision, från prototyper till stora serier."
        breadcrumbs={[
          { name: "Tjänster", href: "/tjanster" },
          { name: "Kantpressning", href: "/tjanster/kantpressning" },
        ]}
        image={IMAGES.workshop3}
      />

      {/* Intro */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Bockning med precision</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Kantpressning för alla behov
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Kantpressning, eller bockning, är en central del av vår metallbearbetning. Med tre
                  kantpressar från Ursviken och Amada kan vi hantera allt från tunna plåtar till
                  tjockare material som kräver hög presskraft.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vår största press levererar 310 ton, vilket ger oss kapacitet att bocka även
                  tjockare material med hög precision. Vi fokuserar alltid på att passformen ska
                  vara optimal, oavsett om det gäller en prototyp eller en serie på tusentals detaljer.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Genom att kombinera laserskärning och kantpressning under samma tak eliminerar vi
                  transporttider mellan underleverantörer och kan leverera snabbare med bättre
                  kontroll över hela processen.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">Våra kantpressar</h3>
                {[
                  { name: "Ursviken kantpress 310 ton", spec: "Vår största press för tjockare material" },
                  { name: "Amada kantpress 160 ton", spec: "Mångsidig press för medelstora jobb" },
                  { name: "Ursviken kantpress 100 ton", spec: "Snabb press för tunnare material" },
                ].map((m) => (
                  <div key={m.name} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
                    <div className="w-2 h-2 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <div>
                      <div className="text-gray-900 font-medium">{m.name}</div>
                      <div className="text-gray-500 text-sm">{m.spec}</div>
                    </div>
                  </div>
                ))}

                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">310</div>
                    <div className="text-gray-500 text-sm">ton max presskraft</div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">3</div>
                    <div className="text-gray-500 text-sm">kantpressar</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16 md:py-20 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Vanliga frågor om kantpressning
              </h2>
              <div className="space-y-4">
                {[
                  { q: "Hur tjockt material kan ni bocka?", a: "Vår största kantpress levererar 310 ton, vilket gör att vi kan bocka även tjockare material. Exakt max tjocklek beror på materialtyp, bocklängd och önskad vinkel." },
                  { q: "Vilka material kan ni kantpressa?", a: "Vi bockar stål, rostfritt stål, aluminium och höghållfasthetsstål. Materialvalet påverkar vilken presskraft och verktyg som krävs." },
                  { q: "Kan ni kombinera kantpressning med laserskärning?", a: "Ja, det är en av våra största fördelar. Vi har både laserskärning och kantpressning under samma tak, vilket eliminerar transporttider och ger bättre kontroll över hela processen." },
                  { q: "Hur lång tid tar leveransen?", a: "Tack vare att vi har skärning och bockning på plats kan vi leverera komplexa projekt inom två arbetsdagar. Enklare jobb kan ofta levereras snabbare." },
                  { q: "Tar ni emot både små och stora serier?", a: "Ja, vi hanterar allt från enstaka prototyper till stora serier. Tre kantpressar ger oss flexibilitet att köra parallellt vid hög belastning." },
                ].map((faq, i) => (
                  <details key={i} className="group border border-gray-200 rounded-lg">
                    <summary className="flex items-center justify-between cursor-pointer p-5 text-gray-900 font-medium hover:bg-gray-50 transition-colors">
                      {faq.q}
                      <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="px-5 pb-5 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Lär dig mer om kantpressning"
        subheading="Fördjupa dig i teknik, materialval och bästa praxis inom kantpressning och bockning."
        articles={[
          { title: "Kantpressning: den kompletta guiden", slug: "kantpressning-guide", excerpt: "Allt du behöver veta om kantpressning – från grunderna till avancerade tekniker." },
          { title: "Toleranser vid plåtbearbetning", slug: "laserskarning-precision-toleranser", excerpt: "Hur toleranser påverkar kvalitet och kostnad vid laserskärning och bockning." },
          { title: "Ytbehandling av laserskurna detaljer", slug: "ytbehandling-plat", excerpt: "Gradning, blästring och lackering – så efterbehandlar du plåtdetaljer." },
          { title: "Materialguide: stål, rostfritt, aluminium", slug: "materialguide-stal-rostfritt-aluminium", excerpt: "Egenskaper och val av rätt material för kantpressning och bockning." },
          { title: "Prototyp till serie", slug: "laserskarning-prototyper", excerpt: "Så tar du en idé från prototyp till serieproduktion med rätt partner." },
          { title: "Ordlista för plåtbearbetning", slug: "plattillverkning-ordlista", excerpt: "Förklaringar av vanliga termer inom laserskärning, kantpressning och svetsning." },
        ]}
      />

      {/* Advantages */}
      <section className="bg-gray-50 py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 text-center">
                Fördelar med kantpressning hos ASPAB
              </h2>
              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                {[
                  { title: "Komplett lösning", desc: "Kombinera laserskärning och kantpressning under ett tak. Mindre transport, snabbare leverans, bättre kontroll." },
                  { title: "Hög kapacitet", desc: "Tre pressar med upp till 310 tons kapacitet ger oss flexibilitet att hantera både små och stora projekt." },
                  { title: "Precision i passform", desc: "Vi är noggranna med att varje bock sitter rätt. Passformen ska vara optimal för att delarna ska passa ihop." },
                  { title: "Snabb leverans", desc: "Tack vare att vi har skärning och bockning på plats kan vi leverera komplexa projekt inom två dagar." },
                ].map((item) => (
                  <div key={item.title} className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="font-heading text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-500 text-sm mt-2 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-lg hover:bg-[#3B82F6] transition-colors"
                >
                  Begär offert <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
